import type { CycleRing } from '@/lib/cycleTypes';
import { MONTH_NAMES } from '@/lib/calendar13Types';

export interface CycleSetConfig {
  id: string;
  name: string;
  description: string;
  cycles: CycleRing[];
  alignments: CycleAlignment[];
}

export interface CycleAlignment {
  sourceCycleId: string;
  targetCycleId: string;
  sourceStage: number;
  targetStage: number;
  sourceSegments?: number;
  targetSegments?: number;
  controlMode?: 'active' | 'response';
  name: string;
}

export type CycleGroupPreset = {
  id: string;
  name: string;
  description: string;
  presetIds: string[];
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const SYNODIC_MONTH = 29.530588853;
const KNOWN_NEW_MOON = new Date('2012-12-13T08:42:00Z').getTime();
const MOON_MONTH_GLYPHS = MONTH_NAMES.map((month, index) => month.glyph ?? String(index + 1));

// Default galactic cycle set
export const DEFAULT_CYCLE_SET: CycleSetConfig = {
  id: 'galactic',
  name: 'Galactic Cycles',
  description: 'Traditional 13-Moon Dreamspell cycles',
  cycles: [],
  alignments: [],
};

// Preset cycle definitions with explicit parent linking
// parentId: which ring this is linked to
// stagesPerParent: how many stages of this ring = 1 stage of parent
export const PRESET_CYCLES: Record<string, Omit<CycleRing, 'currentStage' | 'rotation'>> = {
  days: {
    id: 'days',
    name: '28 Days',
    stages: 28,
    labels: Array(28).fill(0).map((_, i) => String(i + 1)),
    colors: Array(28).fill(0).map((_, i) => `${(i * 13) % 360} 55% 50%`),
    parentId: 'moons',
    stagesPerParent: 28, // 28 days = 1 moon
  },
  moons: {
    id: 'moons',
    name: '13 Moons',
    stages: 13,
    labels: MOON_MONTH_GLYPHS,
    colors: [
      '0 70% 55%', '28 80% 55%', '45 90% 55%', '75 65% 50%',
      '120 55% 45%', '160 60% 45%', '180 65% 45%', '200 70% 50%',
      '230 65% 55%', '260 60% 55%', '290 55% 55%', '320 60% 55%', '270 70% 60%',
    ],
  },
  seals: {
    id: 'seals',
    name: '20 Seals',
    stages: 20,
    labels: ['☀️', '🌙', '💨', '🌱', '🐍', '💀', '🦌', '⭐', '🌊', '🐕',
             '🐒', '🌾', '🦅', '🔮', '🌈', '⚔️', '🌍', '🪞', '🌀', '🌞'],
    colors: Array(20).fill(0).map((_, i) => `${(i * 18) % 360} 65% 50%`),
  },
  colors: {
    id: 'colors',
    name: '4 Colors',
    stages: 4,
    labels: ['R', 'W', 'B', 'Y'],
    colors: ['0 70% 55%', '0 0% 85%', '220 70% 55%', '45 90% 55%'],
    parentId: 'days',
    stagesPerParent: 1, // 1 color segment per day
  },
  plasmas: {
    id: 'plasmas',
    name: '7 Plasmas',
    stages: 7,
    labels: ['Da', 'Se', 'Ga', 'Ka', 'Al', 'Li', 'Si'],
    colors: ['45 90% 55%', '0 70% 55%', '0 0% 85%', '45 90% 55%', '220 70% 55%', '0 70% 55%', '220 70% 55%'],
    parentId: 'days',
    stagesPerParent: 1, // 1 plasma segment per day
  },
  families: {
    id: 'families',
    name: '5 Families',
    stages: 5,
    labels: ['Po', 'Ca', 'Co', 'Si', 'Ga'],
    colors: ['0 70% 55%', '28 80% 55%', '120 55% 45%', '220 70% 55%', '270 60% 55%'],
  },
  quarters: {
    id: 'quarters',
    name: '4 Quarters',
    stages: 4,
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    colors: ['120 55% 45%', '45 90% 55%', '28 80% 55%', '220 70% 55%'],
  },
  weeks: {
    id: 'weeks',
    name: '52 Weeks',
    stages: 52,
    labels: Array(52).fill(0).map((_, i) => String(i + 1)),
    colors: Array(52).fill(0).map((_, i) => `${(i * 7) % 360} 60% 50%`),
    parentId: 'moons',
    stagesPerParent: 4, // 4 weeks = 1 moon
  },
  tzolkin: {
    id: 'tzolkin',
    name: '260 Tzolkin',
    stages: 260,
    labels: Array(260).fill(0).map((_, i) => String(i + 1)),
    colors: Array(260).fill(0).map((_, i) => `${(i * 1.4) % 360} 55% 50%`),
  },
  lunar: {
    id: 'lunar',
    name: 'Lunar Cycle',
    stages: 30, // ~29.5 day synodic month approximated
    waveform: 'sine',
    labels: ['🌑', '🌒', '🌒', '🌒', '🌒', '🌒', '🌒', '🌓', 
             '🌔', '🌔', '🌔', '🌔', '🌔', '🌔', '🌕', '🌕',
             '🌖', '🌖', '🌖', '🌖', '🌖', '🌖', '🌗',
             '🌘', '🌘', '🌘', '🌘', '🌘', '🌘', '🌑'],
    colors: Array(30).fill(0).map((_, i) => `${270 - i * 2} ${40 + Math.sin(i / 30 * Math.PI) * 30}% ${30 + Math.sin(i / 30 * Math.PI) * 40}%`),
    parentId: 'days',
    stagesPerParent: 30 / SYNODIC_MONTH,
  },
  // Solar Second - 60 seconds = 1 minute
  solarSecond: {
    id: 'solarSecond',
    name: 'Solar Second',
    stages: 60,
    labels: Array(60).fill(0).map((_, i) => String(i)),
    colors: Array(60).fill(0).map((_, i) => `${200 + (i * 3) % 60} 50% ${40 + Math.sin(i / 60 * Math.PI * 2) * 20}%`),
    parentId: 'solarMinute',
    stagesPerParent: 60, // 60 seconds = 1 minute
  },
  // Solar Minute - 60 minutes = 1 hour
  solarMinute: {
    id: 'solarMinute',
    name: 'Solar Minute',
    stages: 60,
    labels: Array(60).fill(0).map((_, i) => String(i)),
    colors: Array(60).fill(0).map((_, i) => `${160 + (i * 3) % 60} 55% ${45 + Math.sin(i / 60 * Math.PI * 2) * 15}%`),
    parentId: 'solarHour',
    stagesPerParent: 60, // 60 minutes = 1 hour
  },
  // Solar Hour - 24 hours = 1 day
  solarHour: {
    id: 'solarHour',
    name: 'Solar Hour',
    stages: 24,
    labels: Array(24).fill(0).map((_, i) => String(i)),
    colors: [
      '240 30% 15%', '240 30% 18%', '240 30% 22%', '240 30% 28%', // 0-3 night
      '30 40% 35%', '35 60% 50%', '40 70% 60%', '45 80% 65%', // 4-7 dawn
      '50 85% 70%', '48 90% 72%', '45 95% 75%', '43 95% 78%', // 8-11 morning
      '40 100% 80%', '42 95% 78%', '45 90% 75%', '48 85% 70%', // 12-15 noon/afternoon
      '35 75% 60%', '25 65% 50%', '15 55% 40%', '350 45% 35%', // 16-19 evening
      '280 35% 28%', '260 32% 22%', '250 30% 18%', '240 30% 15%', // 20-23 night
    ],
    parentId: 'sun',
    stagesPerParent: 24, // 24 hours = 1 day
  },
  // Sun cycle - 1 solar day = 1 day in 28-day cycle
  sun: {
    id: 'sun',
    name: 'Solar Day',
    stages: 1, // 1 full rotation = 1 day
    labels: ['☀️'],
    colors: ['45 90% 65%'],
    parentId: 'days',
    stagesPerParent: 1, // 1 solar day = 1 day advance
  },
  earth: {
    id: 'earth',
    name: 'Earth (365d)',
    stages: 365,
    labels: Array(365).fill(0).map((_, i) => String(i + 1)),
    colors: Array(365).fill(0).map((_, i) => `${190 + (i * 0.35) % 90} 55% ${42 + Math.sin(i / 365 * Math.PI * 2) * 12}%`),
    parentId: 'days',
    stagesPerParent: 1,
  },
  // Mercury - 88 day orbit
  mercury: {
    id: 'mercury',
    name: 'Mercury (88d)',
    stages: 88,
    labels: Array(88).fill(0).map((_, i) => String(i + 1)),
    colors: Array(88).fill(0).map((_, i) => `${180 + (i * 2) % 60} 40% ${45 + Math.sin(i / 88 * Math.PI * 2) * 15}%`),
  },
  // Venus - 225 day orbit
  venus: {
    id: 'venus',
    name: 'Venus (225d)',
    stages: 225,
    labels: Array(225).fill(0).map((_, i) => String(i + 1)),
    colors: Array(225).fill(0).map((_, i) => `${40 + (i * 0.5) % 30} 70% ${50 + Math.sin(i / 225 * Math.PI * 2) * 20}%`),
  },
  // Mars - 687 day orbit (simplified to 52 weeks ~364 days for visualization)
  mars: {
    id: 'mars',
    name: 'Mars (687d)',
    stages: 52, // Simplified for visualization
    labels: Array(52).fill(0).map((_, i) => `W${i + 1}`),
    colors: Array(52).fill(0).map((_, i) => `${0 + (i * 3) % 30} 65% ${40 + Math.sin(i / 52 * Math.PI * 2) * 15}%`),
  },
  // Jupiter - 12 year orbit (12 stages)
  jupiter: {
    id: 'jupiter',
    name: 'Jupiter (12y)',
    stages: 12,
    labels: ['Y1', 'Y2', 'Y3', 'Y4', 'Y5', 'Y6', 'Y7', 'Y8', 'Y9', 'Y10', 'Y11', 'Y12'],
    colors: Array(12).fill(0).map((_, i) => `${30 + i * 5} 55% ${50 + Math.sin(i / 12 * Math.PI * 2) * 15}%`),
  },
  // Saturn - 29 year orbit
  saturn: {
    id: 'saturn',
    name: 'Saturn (29y)',
    stages: 29,
    labels: Array(29).fill(0).map((_, i) => `Y${i + 1}`),
    colors: Array(29).fill(0).map((_, i) => `${45 + i * 2} 35% ${40 + Math.sin(i / 29 * Math.PI * 2) * 15}%`),
  },
  uranus: {
    id: 'uranus',
    name: 'Uranus (84y)',
    stages: 84,
    labels: Array(84).fill(0).map((_, i) => `Y${i + 1}`),
    colors: Array(84).fill(0).map((_, i) => `${180 + (i * 1.5) % 80} 45% ${42 + Math.sin(i / 84 * Math.PI * 2) * 12}%`),
  },
  neptune: {
    id: 'neptune',
    name: 'Neptune (165y)',
    stages: 165,
    labels: Array(165).fill(0).map((_, i) => `Y${i + 1}`),
    colors: Array(165).fill(0).map((_, i) => `${220 + (i * 0.6) % 70} 42% ${38 + Math.sin(i / 165 * Math.PI * 2) * 10}%`),
  },
  atomicHour: {
    id: 'atomicHour',
    name: 'Atomic Hour',
    stages: 24,
    labels: Array(24).fill(0).map((_, i) => String(i).padStart(2, '0')),
    colors: Array(24).fill(0).map((_, i) => `${190 + i * 3} 60% ${42 + Math.sin(i / 24 * Math.PI * 2) * 10}%`),
    parentId: 'days',
    stagesPerParent: 24,
  },
  atomicMinute: {
    id: 'atomicMinute',
    name: 'Atomic Minute',
    stages: 60,
    labels: Array(60).fill(0).map((_, i) => String(i).padStart(2, '0')),
    colors: Array(60).fill(0).map((_, i) => `${205 + (i * 2) % 60} 62% ${45 + Math.sin(i / 60 * Math.PI * 2) * 10}%`),
    parentId: 'atomicHour',
    stagesPerParent: 60,
  },
  atomicSecond: {
    id: 'atomicSecond',
    name: 'Atomic Second',
    stages: 60,
    labels: Array(60).fill(0).map((_, i) => String(i).padStart(2, '0')),
    colors: Array(60).fill(0).map((_, i) => `${220 + (i * 2) % 60} 64% ${48 + Math.sin(i / 60 * Math.PI * 2) * 10}%`),
    parentId: 'atomicMinute',
    stagesPerParent: 60,
  },
  'gregorian-leap': {
    id: 'gregorian-leap',
    name: 'Gregorian Leap Cycle',
    stages: 4,
    labels: ['Leap Year', 'Year 1', 'Year 2', 'Year 3'],
    colors: ['120 55% 45%', '45 85% 60%', '28 80% 55%', '220 70% 55%'],
  },
};

export const SOLAR_SYSTEM_GROUP: CycleGroupPreset = {
  id: 'solar-system',
  name: 'Solar System',
  description: 'Planetary orbits with lunar position tracking.',
  presetIds: ['sun', 'lunar', 'mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'],
};

export const EARTH_PLANETS_GROUP: CycleGroupPreset = {
  id: 'earth-planets',
  name: 'Earth + All Planets',
  description: 'Earth cycle plus all major planetary orbital cycles.',
  presetIds: ['earth', 'mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'],
};

export const ATOMIC_CLOCK_GROUP: CycleGroupPreset = {
  id: 'atomic-clock',
  name: 'Atomic Clock',
  description: 'Nested atomic hour, minute, and second cycles.',
  presetIds: ['atomicHour', 'atomicMinute', 'atomicSecond'],
};

export const CYCLE_GROUP_PRESETS: CycleGroupPreset[] = [
  SOLAR_SYSTEM_GROUP,
  EARTH_PLANETS_GROUP,
  ATOMIC_CLOCK_GROUP,
];

// Calculate position based on day of year and cycle length
// dayOfYear is 1-indexed (1-365), returns 0-indexed stage position
export function calculateCyclePosition(dayOfYear: number, stages: number, offset: number = 0): number {
  // Convert 1-indexed dayOfYear to 0-indexed for stage calculation
  return ((dayOfYear - 1) + offset) % stages;
}

// Create a cycle ring from preset with current position
export function createCycleFromPreset(
  presetId: string,
  dayOfYear: number,
  offset: number = 0
): CycleRing | null {
  const preset = PRESET_CYCLES[presetId];
  if (!preset) return null;
  
  let currentStage: number;
  
  if (preset.id === 'days' || preset.stages === 28) {
    // Days ring: position = (dayInMonth - 1), dayInMonth = ((dayOfYear-1) % 28) + 1
    currentStage = (dayOfYear - 1) % 28;
  } else if (preset.id === 'moons' || (preset.stages === 13 && (preset.name ?? '').includes('Moon'))) {
    // Moons ring: position = month - 1, month = floor((dayOfYear-1) / 28) + 1
    currentStage = Math.floor((dayOfYear - 1) / 28);
  } else {
    currentStage = calculateCyclePosition(dayOfYear, preset.stages, offset);
  }
  
  const segmentAngle = 360 / preset.stages;
  
  return {
    ...preset,
    currentStage,
    rotation: -currentStage * segmentAngle,
  };
}

export function getLeapCyclePosition(date: Date, fractionOfDay: number = 0) {
  const anchor = new Date(2000, 0, 1); // Known leap year
  const diffDays = Math.floor((date.getTime() - anchor.getTime()) / MS_PER_DAY);
  const cycleLength = 365 * 3 + 366; // 4-year block
  const normalized = ((diffDays % cycleLength) + cycleLength) % cycleLength;

  const yearLengths = [366, 365, 365, 365];
  let remaining = normalized;
  let stageIndex = 0;

  while (stageIndex < yearLengths.length && remaining >= yearLengths[stageIndex]) {
    remaining -= yearLengths[stageIndex];
    stageIndex++;
  }

  const stageLength = yearLengths[stageIndex] ?? 365;
  const progress = Math.min(remaining + fractionOfDay, stageLength) / stageLength;

  return { stageIndex, progress };
}

export function createGregorianLeapCycleRing(date: Date = new Date()): CycleRing {
  const preset = PRESET_CYCLES['gregorian-leap'];
  const { stageIndex, progress } = getLeapCyclePosition(date);
  const segmentAngle = 360 / preset.stages;

  return {
    ...preset,
    currentStage: stageIndex,
    rotation: -(stageIndex + progress) * segmentAngle,
  };
}

// Create lunar cycle ring with proper astronomical position
export function createLunarCycleRing(date: Date = new Date()): CycleRing {
  const preset = PRESET_CYCLES['lunar'];
  const daysSinceNewMoon = (date.getTime() - KNOWN_NEW_MOON) / (1000 * 60 * 60 * 24);
  let phase = (daysSinceNewMoon % SYNODIC_MONTH) / SYNODIC_MONTH;
  if (phase < 0) phase += 1;
  
  const currentStage = Math.floor(phase * preset.stages);
  const segmentAngle = 360 / preset.stages;
  
  return {
    ...preset,
    currentStage,
    rotation: -phase * 360, // Continuous rotation based on exact phase
  };
}

export function buildCycleRingsFromPresets(
  presetIds: string[],
  dayOfYear: number,
  gregorianDate: Date
): CycleRing[] {
  return presetIds
    .map((presetId, index) => {
      if (presetId === 'gregorian-leap') {
        return { ...createGregorianLeapCycleRing(gregorianDate), displayOrder: index };
      }

      if (presetId === 'lunar') {
        return { ...createLunarCycleRing(gregorianDate), displayOrder: index };
      }

      const cycle = createCycleFromPreset(presetId, dayOfYear);
      if (!cycle) return null;

      return { ...cycle, displayOrder: index };
    })
    .filter(Boolean) as CycleRing[];
}

// Canonical display order for the default rings.
// Lower displayOrder = OUTERMOST ring (index 0 in sorted array = largest radius).
// Applied when loading saved rings to ensure order changes take effect for existing users.
export const CANONICAL_RING_DISPLAY_ORDER: Record<string, number> = {
  lunar: 0,            // outermost
  moons: 1,
  days: 2,
  plasmas: 3,
  colors: 4,
  seals: 5,
  families: 6,
  quarters: 7,
  jupiter: 8,
  'gregorian-leap': 9, // innermost
};

// Get default cycle set - dayOfYear is 1-indexed (1-365)
export function getDefaultCycleSet(dayOfYear: number, gregorianDate: Date = new Date()): CycleSetConfig {
  return {
    ...DEFAULT_CYCLE_SET,
    cycles: [
      { ...createLunarCycleRing(), displayOrder: 0 },                          // Outer: Lunar synodic cycle (~29.5 days)
      { ...createCycleFromPreset('moons', dayOfYear)!, displayOrder: 1 },     // 13 moons (1 year period)
      { ...createCycleFromPreset('days', dayOfYear)!, displayOrder: 2 },      // 28 days (1 month period)
      { ...createCycleFromPreset('plasmas', dayOfYear)!, displayOrder: 3 },   // 7 plasmas (weekly)
      { ...createCycleFromPreset('colors', dayOfYear)!, displayOrder: 4 },    // 4 colors (4-day cycle)
      { ...createCycleFromPreset('seals', dayOfYear)!, displayOrder: 5 },     // 20 seals
      { ...createCycleFromPreset('families', dayOfYear)!, displayOrder: 6 },  // 5 families
      { ...createCycleFromPreset('quarters', dayOfYear)!, displayOrder: 7 },  // 4 quarters
      { ...createCycleFromPreset('jupiter', dayOfYear)!, displayOrder: 8 },   // Jupiter 12-year cycle
      { ...createGregorianLeapCycleRing(gregorianDate), displayOrder: 9 },    // Gregorian 4-year leap cycle
    ],
  };
}

// Save cycle set to localStorage
export function saveCycleSet(cycleSet: CycleSetConfig): void {
  const sets = loadAllCycleSets();
  const idx = sets.findIndex(s => s.id === cycleSet.id);
  if (idx >= 0) {
    sets[idx] = cycleSet;
  } else {
    sets.push(cycleSet);
  }
  localStorage.setItem('cycle-sets', JSON.stringify(sets));
}

// Load all cycle sets from localStorage
export function loadAllCycleSets(): CycleSetConfig[] {
  const saved = localStorage.getItem('cycle-sets');
  return saved ? JSON.parse(saved) : [];
}

// Load active cycle set
export function loadActiveCycleSet(dayOfYear: number, gregorianDate: Date = new Date()): CycleSetConfig {
  const activeId = localStorage.getItem('active-cycle-set');
  const sets = loadAllCycleSets();
  const active = sets.find(s => s.id === activeId);
  return active || getDefaultCycleSet(dayOfYear, gregorianDate);
}

// Set active cycle set
export function setActiveCycleSet(id: string): void {
  localStorage.setItem('active-cycle-set', id);
}
